import { AdBuilderImage } from "../../types/ad-builder-image";
import { Platform } from "../../types/ad-platforms";
import { Comment } from "../../types/comment";
import { CamelCasedPropertiesDeep } from "../../types/conversion-types";
import { ImageServerResponse } from "../../types/image";
import { Tag } from "../../types/tag";
import { AspectRatio } from "../../types/aspect-ratios";

export type AdAssetGroupStatus =
  | "assembling"
  | "new"
  | "draft"
  | "ready_for_review"
  | "ready_to_publish"
  | "revising"
  | "publishing"
  | "published"
  | "partial_publish"
  | "partial_failure"
  | "failed";

// Re-export AspectRatio and isAspectRatio from centralized location for backward compatibility

export interface AdAssetServerImage {
  id: number;
  url: string;
  height: number;
  width: number;
  type: AspectRatio;
  ad_asset_id: number;
}

export interface AdAssetGroupServerResponse {
  id: number;
  brand_id: number;
  status: AdAssetGroupStatus;
  preview_image_url?: string;
  preview_video_url?: string;
  name: string;
  comments: Comment[];
  platforms: Platform[];
  aspect_ratios: string[];
  ad_asset_count: number;
  ad_image_count: number;
  description?: string;
  tracking_id_start: number;
  google_ad_group_id?: number;
  meta_ad_set_id?: number;
  meta_ad_account_id?: string;
  google_campaign_name?: string;
  google_ad_group_name?: string;
  google_ad_group_url?: string;
  msan_campaign_name?: string;

  msan_ad_group_name?: string;
  msan_ad_group_url?: string;
  meta_campaign_name?: string;
  meta_ad_set_name?: string;
  meta_ad_set_url?: string;
  preferred_aspect_ratio_for_placements?: "feed" | "square";
  created_at: string;
  updated_at: string;
  locked: boolean;
  status_counts: Record<
    "decision_pending" | "approved" | "failed" | "revising" | "assembling" | "rejected" | "need_edits",
    number
  >;
  unresolved_comment_asset_count?: number;
  theme_id?: number;
}

export interface VideoPlacementServerResponse {
  id: number;
  brand_id: number;
  ad_builder_video_id: number;
  width: number;
  height: number;
  aspect_ratio: string;
  templated_video_path: string | null;
  untemplated_video_path: string | null;
  signed_url: string | null;
  signed_untemplated_url: string | null;
}

// Ad asset approval statuses
export type AdAssetStatus = "approved" | "rejected" | "decision_pending" | "saved_for_later" | "need_edits";

export enum AdAssetStatusEnum {
  APPROVED = "approved",
  REJECTED = "rejected",
  DECISION_PENDING = "decision_pending",
  SAVED_FOR_LATER = "saved_for_later",
  NEED_EDITS = "need_edits"
}

/** Regional comment thread from API (snake_case). Returned on ad_assets when backend transfers image comments at submission. */
export interface AdAssetThreadServerResponse {
  id: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  aspect_ratio: string;
  comments: Comment[];
  resolved: boolean;
}

export interface AdAssetServerResponse {
  id: number;
  name: string;
  ad_builder_image: AdBuilderImage;
  images: AdAssetServerImage[];
  comments: Comment[];
  /** Regional comment threads; populated when backend transfers image comments to ad asset at submission */
  threads?: AdAssetThreadServerResponse[];
  is_deleted: boolean;
  is_resolved: boolean;
  // New status field for approval workflow
  status?: AdAssetStatus;
  facebook_headlines: string[];
  google_headlines: string[];
  google_descriptions: string[];
  facebook_description: string;
  facebook_call_to_action_type?: string;
  google_call_to_action_type?: string;
  google_destination_url?: string;
  facebook_destination_url?: string;
  google_long_headline: string;
  facebook_bodies: string[];
  facebook_placement_settings?: FacebookPlacementSettings;
  msan_headlines: string[];
  msan_long_headline: string;
  msan_descriptions: string[];
  msan_call_to_action_type?: string;
  msan_destination_url?: string;
  image_folder_path?: string;
  ad_asset_group_id: number;
  creative_id?: number | null;
  brand_id: number;
  updated_at: string;
  created_at: string;
  video_id?: number | null;
  videos?: VideoPlacementServerResponse[];
}

export type AdAssetImage = CamelCasedPropertiesDeep<Omit<AdAssetServerImage, "url">> & {
  src: string;
};

export type Tags = CamelCasedPropertiesDeep<Omit<Tag, "src">> & {
  src: string;
};

export type AdImage = CamelCasedPropertiesDeep<Omit<ImageServerResponse, "src">> & {
  src: string;
};

export type AdAssetGroup = CamelCasedPropertiesDeep<AdAssetGroupServerResponse>;

// // TODO add placement settings
export type AdAssetBulkUpdateRequest = Partial<
  Omit<AdAssetServerResponse, "id" | "images" | "comments" | "is_deleted">
>;

// not a type alias because of getAdCopyData in helpers.ts
// That function cannot narrow a type alias to just the extracted fields
export interface AdAsset extends CamelCasedPropertiesDeep<
  Omit<AdAssetServerResponse, "images" | "facebook_placement_settings">
> {
  images: AdAssetImage[];
  facebookPlacementSettings?: FacebookPlacementSettings;
  adBuilderImage: AdBuilderImage;
}

export enum PublisherPlatforms {
  FACEBOOK = "facebook",
  INSTAGRAM = "instagram",
  AUDIENCE_NETWORK = "audience_network",
  MESSENGER = "messenger"
}

export enum FacebookPlacementPositions {
  FEED = "feed",
  RIGHT_HAND_COLUMN = "right_hand_column",
  MARKETPLACE = "marketplace",
  VIDEO_FEEDS = "video_feeds",
  STORY = "story",
  SEARCH = "search",
  INSTREAM_VIDEO = "instream_video",
  FACEBOOK_REELS = "facebook_reels",
  FACEBOOK_REELS_OVERLAY = "facebook_reels_overlay",
  BIZ_DISCO_FEED = "biz_disco_feed"
}

export enum InstagramPlacementPositions {
  STREAM = "stream",
  STORY = "story",
  EXPLORE = "explore",
  EXPLORE_HOME = "explore_home",
  REELS = "reels",
  PROFILE_FEED = "profile_feed",
  IG_SEARCH = "ig_search",
  PROFILE_REELS = "profile_reels"
}

export enum MessengerPlacementPositions {
  MESSENGER_HOME = "messenger_home",
  SPONSORED_MESSAGES = "sponsored_messages",
  STORY = "story"
}

export enum AudienceNetworkPlacementPositions {
  CLASSIC = "classic",
  REWARDED_VIDEO = "rewarded_video"
}

export type EnabledPlatforms = {
  [key in PublisherPlatforms]: boolean;
};

export interface FacebookPlacementSettings {
  platforms: EnabledPlatforms;
  facebook_positions: FacebookPlacementPositions[];
  instagram_positions: InstagramPlacementPositions[];
  audience_network_positions: AudienceNetworkPlacementPositions[];
  messenger_positions: MessengerPlacementPositions[];
}

export { isAspectRatio, type AspectRatio } from "../../types/aspect-ratios";
